import React, { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useMediaQuery } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import SearchAndFilterContainer from 'src/pages/more/pages/fieldInventoryDashboard/searchAndFilterContainer';
import { sizes } from 'src/styles/Media.styled';
import {
    getInventoryPreference,
    getInventoryList,
    resetSetsInventoryExport,
    setSetsFilterData,
    setsInventoryExport,
    setSetsSearchParams,
} from 'src/pages/receiveSets/redux/actions';
import { getSetDetails, resetSetDetails } from 'src/pages/more/redux/actions';
import BatchPopup from 'src/common/refactoredComponents/batchPopup/BatchPopup';
import Backdrop from 'src/common/refactoredComponents/backdrop/Backdrop';
import Spinner from 'src/common/refactoredComponents/spinner/Spinner';
import SetsResultsHeader from './SetsResultsHeader';
import SetsTable from './SetsTable';
import SetsMobileTable from './SetsMobileTable';
import {
    SETS_PAGE_LIMIT,
    SETS_SEARCH_TYPES,
    SETS_TABLE_SORT_CODE_MAP,
    SETS_DEFAULT_TABLE_SORT_FIELD,
    SETS_DEFAULT_TABLE_SORT_ORDER,
} from './setsInventoryConstants';
import {
    SetsInventoryContainer,
    EmptyResultsMessage,
    SearchAndResultsCard,
} from './index.styled';
import { useEffectWithErrorLogging } from 'src/utils/useEffectWithErrorLogging';
import { fetchFilterData } from 'src/pages/receiveSets/redux/actions';
const MIME_TO_EXT = {
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
    'application/vnd.ms-excel': 'xls',
    'text/csv': 'csv',
    'application/csv': 'csv',
    'text/plain': 'txt',
};

const downloadBlobFile = (blob, baseFileName = 'sets-inventory') => {
    if (!blob || typeof window === 'undefined') return;

    const mimeType = blob.type || 'application/octet-stream';
    const ext = MIME_TO_EXT[mimeType.split(';')[0].trim()] || 'xlsx';
    const fileName = `${baseFileName}.${ext}`;
    const typedBlob = blob.type ? blob : new Blob([blob], { type: mimeType });
    const url = window.URL.createObjectURL(typedBlob);
    const link = document.createElement('a');

    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
};

const SEARCH_TERM_MAP = {
    setOrDescription: 'setNumber',
    ordernum: 'orderNumber',
};

const SetsInventory = ({
    onOpenFilters = () => {},
}) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const isMobileView = useMediaQuery(`(max-width:${sizes.mobileDevice}px)`);
    const bannerHeight = useSelector(
        (state) => state.loginReducer.bannerHeight
    );
    const hasBanner = !!bannerHeight;
    const searchTypeOptions = useMemo(
        () =>
            SETS_SEARCH_TYPES.map(
                ({
                    value,
                    labelKey,
                    mobileLabelKey,
                    placeholderKey,
                    mobilePlaceholderKey,
                }) => ({
                    label: t(
                        isMobileView && mobileLabelKey
                            ? mobileLabelKey
                            : labelKey
                    ),
                    placeholderKey:
                        isMobileView && mobilePlaceholderKey
                            ? mobilePlaceholderKey
                            : placeholderKey,
                    code: value,
                })
            ),
        [t, isMobileView]
    );
    const [first, setFirst] = useState(0);
    const [currentPageNum, setCurrentPageNum] = useState(0);
    const [searchText, setSearchText] = useState('');
    const [searchType, setSearchType] = useState(searchTypeOptions[0].code);
    const [sortField, setSortField] = useState(SETS_DEFAULT_TABLE_SORT_FIELD);
    const [sortOrder, setSortOrder] = useState(SETS_DEFAULT_TABLE_SORT_ORDER);

    const {
        data: inventoryListResponse,
        loadingSetsExport = false,
        exportData,
    } = useSelector((state) => state.inventoryListReducer || {});
    const { appliedFilterNames = [] } = useSelector(
        (state) => state.inventoryPreferenceReducer.setsFilterData || {},
        shallowEqual
    );
     const { districtFilters} = useSelector(
            (state) => state.consignedInventoryReducer.filterData || {},
            shallowEqual
        );
    const inventoryListData = inventoryListResponse?.inventory;
    const {
        loading: isSetDetailsLoading,
        data: setDetailsData,
        error: setDetailsError,
    } = useSelector((state) => state.setDetailsReducer);
    const [activeViewSet, setActiveViewSet] = useState(null);
    const [isBatchPopupOpen, setIsBatchPopupOpen] = useState(false);
    const inventoryListLoading = useSelector(
        (state) => state.inventoryListReducer.loading
    );
    const setsSearchParams = useSelector(
        (state) => state.inventoryPreferenceReducer.setsSearchParams || {}
    );
    const setsFilterData = useSelector(
        (state) => state.inventoryPreferenceReducer.setsFilterData || {}
    );
    const totalRecords =
        inventoryListResponse?.totalNumberOfResults;

    const buildListParams = ({ term = searchText, type = searchType, page = currentPageNum, ...extraParams } = {}) => {
        const trimmed = (term || '').trim();
        const params = {
            ...setsFilterData,
            ...extraParams,
            currentPage: page,
            pageSize: SETS_PAGE_LIMIT,
            sortCode:
                SETS_TABLE_SORT_CODE_MAP[sortField] ||
                SETS_TABLE_SORT_CODE_MAP[SETS_DEFAULT_TABLE_SORT_FIELD],
            sortOrder,
            // An applied district wins; the single-district default only fills in when none is applied
            currentDistrict: extraParams.currentDistrict || setsFilterData.currentDistrict || (districtFilters.length == 1 ? districtFilters[0]?.districtCode : '')
        };
        delete params.appliedFilterNames;

        if (trimmed) {
            params.searchTerm = SEARCH_TERM_MAP[type] || type;
            params.searchValue = trimmed;
        }

        return params;
    };

    useEffectWithErrorLogging(() => {
        dispatch(fetchFilterData('districtFilters'));
        dispatch(fetchFilterData('territoryFilters'));
        dispatch(fetchFilterData('accountFilters'));
    }, []);

    useEffectWithErrorLogging(() => {
        dispatch(getInventoryPreference());
    }, [dispatch]);

    useEffectWithErrorLogging(() => {
        if(districtFilters.length == 0) return;
        const { appliedFilterNames: _appliedFilterNames, ...appliedFilter } = setsFilterData;
        dispatch(getInventoryList(buildListParams({ page: 0, ...appliedFilter })));
    }, [dispatch, sortField, sortOrder, districtFilters]);

    useEffectWithErrorLogging(() => {
        if (!loadingSetsExport && exportData) {
            const fileName = `sets-inventory-${new Date()
                .toISOString()
                .slice(0, 10)}`;
            downloadBlobFile(
                exportData instanceof Blob ? exportData : new Blob([exportData]),
                fileName
            );
            dispatch(resetSetsInventoryExport());
        }
    }, [dispatch, loadingSetsExport, exportData]);

    useEffectWithErrorLogging(() => {
        if (activeViewSet && setDetailsData && !isSetDetailsLoading && !setDetailsError) {
            setIsBatchPopupOpen(true);
        }
    }, [activeViewSet, setDetailsData, isSetDetailsLoading, setDetailsError]);

    useEffectWithErrorLogging(() => {
        if (setDetailsError) {
            setIsBatchPopupOpen(false);
            setActiveViewSet(null);
        }
    }, [setDetailsError]);

    const handleSearch = (term, type, isClear) => {
        const trimmed = (term || '').trim();

        if (!trimmed && !isClear) {
            return;
        }

        setSearchText(trimmed);
        if (type) setSearchType(type);
        setFirst(0);
        setCurrentPageNum(0);

        const searchParams = trimmed
            ? {
                  searchTerm: SEARCH_TERM_MAP[type || searchType] || type || searchType,
                  searchValue: trimmed,
              }
            : {};
        dispatch(setSetsSearchParams(searchParams));

        dispatch(
            getInventoryList(
                buildListParams({
                    term: isClear ? '' : trimmed,
                    type: type || searchType,
                    page: 0,
                })
            )
        );
    };

    const handleSortChange = (event) => {
        const nextField = SETS_TABLE_SORT_CODE_MAP[event?.sortField]
            ? event.sortField
            : SETS_DEFAULT_TABLE_SORT_FIELD;
        const nextOrder = event?.sortOrder === -1 ? 'DESC' : 'ASC';

        if (nextField === sortField && nextOrder === sortOrder) {
            return;
        }

        setSortField(nextField);
        setSortOrder(nextOrder);
        setFirst(0);
        setCurrentPageNum(0);
    };

    const handlePageChange = (event) => {
        setFirst(event.first);
        setCurrentPageNum(event.page);
        dispatch(getInventoryList(buildListParams({ page: event.page })));
    };

    const handleViewSet = (row = {}) => {
        setIsBatchPopupOpen(false);
        setActiveViewSet(row);
        dispatch(resetSetDetails());
        dispatch(
            getSetDetails({
                batchNo: row.setBatch || row.batchId || row.batchNo,
                setNo: row.setNo || row.setNumber,
            })
        );
    };

    const handleBatchClick = (event, row) => {
        event.preventDefault();
        event.stopPropagation();
        handleViewSet(row);
    };
    const handleBatchPopupClose = () => {
        setIsBatchPopupOpen(false);
        setActiveViewSet(null);
        dispatch(resetSetDetails());
    };

    const getPlaceholder = (selectedType) => {
        const fresh = searchTypeOptions.find((o) => o.code === selectedType?.code) || selectedType;
        return fresh?.placeholderKey ? t(fresh.placeholderKey) : `Enter a ${fresh?.label}`;
    };

    const exportToExcel = () => {
        const { appliedFilterNames: _appliedFilterNames, ...appliedFilter } = setsFilterData;
        const trimmedSearchText = searchText.trim();
        const payload = {
            allocated: false,
            sortCode:
                SETS_TABLE_SORT_CODE_MAP[sortField] ||
                SETS_TABLE_SORT_CODE_MAP[SETS_DEFAULT_TABLE_SORT_FIELD],
            sortOrder,
            ...appliedFilter
        };

        if (trimmedSearchText) {
            payload.searchTerm = SEARCH_TERM_MAP[searchType] || searchType;
            payload.searchValue = trimmedSearchText;
        }

        dispatch(setsInventoryExport(payload));
    };

    const handleClearFilters = () => {
        const districtCode = districtFilters[0]?.districtCode !=='All' &&  districtFilters[0].districtCode;
       districtCode ?? (setsSearchParams.currentDistrict = districtCode);
        dispatch(getInventoryList(setsSearchParams));
        dispatch(setSetsFilterData({}));
        dispatch(fetchFilterData(
            'accountFilters',
            {},
            ['homeLocationAccount', 'currentLocationAccount']
        ));
    };

    return (
        <SetsInventoryContainer isMobileView={isMobileView} hasBanner={hasBanner} id="receiveSets-setsInventory-container">
            {!isMobileView && (<><SearchAndFilterContainer
                    key={isMobileView ? 'mobile' : 'desktop'}
                    onSearch={handleSearch}
                    searchText={searchText}
                    searchType={searchType}
                    searchTypeOptions={searchTypeOptions}
                    getPlaceholder={getPlaceholder}
                    searchLabel={t('receiveSets.setsInventory.searchLabel')}
                    searchId="sets"
                    truncateSearchTypeLabel={true}
                    searchBoxMarginDesktop={!isMobileView ? '8px 8px' : '0'}
                />
                <SetsResultsHeader
                    resultsCount={totalRecords}
                    onExport={exportToExcel}
                    loadingExport={loadingSetsExport}
                    setsAppliedFilters={appliedFilterNames}
                    onClearFilters={handleClearFilters}
                    onOpenFilters={onOpenFilters}
                /></>)}
            {isMobileView && (<SearchAndResultsCard>
                <SearchAndFilterContainer
                    key={isMobileView ? 'mobile' : 'desktop'}
                    onSearch={handleSearch}
                    searchText={searchText}
                    searchType={searchType}
                    searchTypeOptions={searchTypeOptions}
                    getPlaceholder={getPlaceholder}
                    searchLabel={t('receiveSets.setsInventory.searchLabel')}
                    searchId="sets"
                    containerStyle={{ marginLeft: '4px', width: '100%' }}
                    truncateSearchTypeLabel={true}
                    searchLabelFontSize={"16px"}
                    // containerStyle={isMobileView ? { padding: '0px', boxShadow: '0px', background: 'none' } : {}}
                />
                <SetsResultsHeader
                    resultsCount={totalRecords}
                    onExport={exportToExcel}
                    loadingExport={loadingSetsExport}
                    setsAppliedFilters={appliedFilterNames}
                    onClearFilters={handleClearFilters}
                    onOpenFilters={onOpenFilters}
                />
            </SearchAndResultsCard>)}
            
            {inventoryListLoading && (
                <Backdrop showBackdrop={true} isModal={true} loaderBackground>
                    <Spinner
                        id="setsInventory-search-loadingSpinner"
                        animation="border"
                        variant="primary"
                        small
                    />
                </Backdrop>
            )}
            {!inventoryListLoading && totalRecords === 0 && (
                <EmptyResultsMessage>
                    {t('receiveSets.setsInventory.noResultsMessage')}
                </EmptyResultsMessage>
            )}
            {totalRecords > 0 &&
                (isMobileView ? (
                    <SetsMobileTable
                        rows={inventoryListData || []}
                        totalRecords={totalRecords}
                        recordsPerPage={SETS_PAGE_LIMIT}
                        first={first}
                        onPageChange={handlePageChange}
                        onViewSet={handleViewSet}
                        handleBatchClick={handleBatchClick}
                    />
                ) : (
                    <SetsTable
                        rows={inventoryListData || []}
                        totalRecords={totalRecords}
                        recordsPerPage={SETS_PAGE_LIMIT}
                        first={first}
                        onPageChange={handlePageChange}
                        onSortChange={handleSortChange}
                        sortField={sortField}
                        sortOrder={sortOrder === 'DESC' ? -1 : 1}
                        onViewSet={handleViewSet}
                        handleBatchClick={handleBatchClick}
                    />
                ))}
            {isSetDetailsLoading &&
            activeViewSet &&
            typeof document !== 'undefined'
                ? createPortal(
                      <Backdrop
                          showBackdrop={true}
                          isModal={true}
                          loaderBackground
                      >
                          <Spinner
                              id="setsInventory-viewSet-loadingSpinner"
                              animation="border"
                              variant="primary"
                              small
                          />
                      </Backdrop>,
                      document.body
                  )
                : null}
            {isBatchPopupOpen &&
            activeViewSet &&
            typeof document !== 'undefined'
                ? createPortal(
                      <BatchPopup
                          batchDetails={setDetailsData}
                          isLoading={isSetDetailsLoading}
                          title={activeViewSet.batchNo || activeViewSet.batchId}
                          isOpen={isBatchPopupOpen}
                          onClose={handleBatchPopupClose}
                          batchId={
                              activeViewSet.setBatch || activeViewSet.batchId
                          }
                          setNumber={
                              activeViewSet.setNo || activeViewSet.setNumber
                          }
                      />,
                      document.body
                  )
                : null}
        </SetsInventoryContainer>
    );
};

export default SetsInventory;
